// src/services/attendanceService.js
import { collection, addDoc, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Format date as MM-DD-YYYY
 */
const formatDate = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

/**
 * Check if student already has attendance record for today
 */
export const getTodaysAttendance = async (studentId) => {
  const today = formatDate();
  const attendanceRef = collection(db, 'attendance');
  const q = query(
    attendanceRef,
    where('studentId', '==', studentId),
    where('date', '==', today)
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  
  return {
    id: snapshot.docs[0].id,
    ...snapshot.docs[0].data()
  };
};

/**
 * Check in a student
 */
export const checkInStudent = async (student, staffName, signature, notes = '') => {
  const today = formatDate();
  const now = new Date().toISOString();
  
  // Check if already has attendance record
  const existing = await getTodaysAttendance(student.id);
  
  if (existing) {
    // Update existing record
    const attendanceRef = doc(db, 'attendance', existing.id);
    await updateDoc(attendanceRef, {
      checkInTime: now,
      checkInBy: staffName,
      checkInSignature: signature,
      checkInNotes: notes,
      status: 'checked-in'
    });
    return existing.id;
  } else {
    // Create new record
    const attendanceRef = collection(db, 'attendance');
    const docRef = await addDoc(attendanceRef, {
      studentId: student.id,
      studentName: `${student.studentFirstName || student.firstName} ${student.studentLastName || student.lastName}`,
      grade: student.grade,
      program: student.programSelection,
      date: today,
      checkInTime: now,
      checkInBy: staffName,
      checkInSignature: signature,
      checkInNotes: notes,
      checkOutTime: null,
      checkOutBy: null,
      checkOutSignature: null,
      checkOutNotes: null,
      status: 'checked-in'
    });
    return docRef.id;
  }
};

/**
 * Check out a student
 */
export const checkOutStudent = async (studentId, staffName, signature, notes = '') => {
  const now = new Date().toISOString();
  
  // Get today's attendance record
  const attendance = await getTodaysAttendance(studentId);
  
  if (!attendance) {
    throw new Error('No check-in record found for today. Please check in first.');
  }
  
  // Update with check-out info
  const attendanceRef = doc(db, 'attendance', attendance.id);
  await updateDoc(attendanceRef, {
    checkOutTime: now,
    checkOutBy: staffName,
    checkOutSignature: signature,
    checkOutNotes: notes,
    status: 'checked-out'
  });
  
  return attendance.id;
};

/**
 * Get all attendance records for today
 */
export const getTodaysAllAttendance = async () => {
  const today = formatDate();
  const attendanceRef = collection(db, 'attendance');
  const q = query(attendanceRef, where('date', '==', today));
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};